/**
 * Centralized TanStack Query keys factory.
 *
 * Why a factory (vs inline arrays in feature hooks)?
 * 1. Single source of truth — refactoring a key shape touches one file.
 * 2. Type-safe `as const` tuples — `queryClient.invalidateQueries({ queryKey })`
 *    gets exact literal types, not `string[]`.
 * 3. Architectural invariant: ALL workspace-scoped keys MUST start with the
 *    literal string `'ws'`. This lets `useWorkspaceStore.switchWorkspace`
 *    wipe every workspace's cache in one call:
 *
 *      queryClient.removeQueries({ queryKey: ['ws'] })
 *
 *    See architecture §3.3 / §4.3, PRD constraint C-09 (workspace switch must
 *    not leak previous-ws data into the new ws). Inlining keys in features
 *    risks omitting the `'ws'` prefix and silently leaking cross-workspace
 *    data — the factory makes this impossible by construction.
 *
 * Convention:
 *   - `qk.auth.*`   → session-scoped, survives workspace switch.
 *   - `qk.global.*` → version/config/workspace-list, ws-independent.
 *   - `qk.admin.*`  → admin endpoints, intentionally cross-workspace.
 *   - `qk.ws(wsId).<domain>.<view>(...)` → workspace-scoped; wsId may be
 *     `null` while the active workspace is being resolved (initial mount,
 *     between switches). Hooks should pass `enabled: wsId !== null` to
 *     avoid firing requests with a null ws.
 *
 * This is a starter — feature epics (E04+) will extend `qk.ws(...)` and
 * `qk.admin` with new domains. Keep the `'ws'` prefix invariant intact.
 */

// Filter object types are kept loose here; feature epics will tighten them
// once endpoint-specific filter shapes are defined.
type Filters = Readonly<Record<string, unknown>>

export const qk = {
  auth: {
    me: () => ['auth', 'me'] as const,
  },
  ws: (wsId: number | null) => ({
    vsl: {
      runs: () => ['ws', wsId, 'vsl', 'runs'] as const,
      run: (id: number) => ['ws', wsId, 'vsl', 'run', id] as const,
      bases: (kind: string) => ['ws', wsId, 'vsl', 'bases', kind] as const,
      scripts: () => ['ws', wsId, 'vsl', 'scripts'] as const,
      script: (id: number) => ['ws', wsId, 'vsl', 'script', id] as const,
    },
    voices: {
      list: () => ['ws', wsId, 'voices', 'list'] as const,
      detail: (id: number) => ['ws', wsId, 'voices', 'detail', id] as const,
      history: () => ['ws', wsId, 'voices', 'history'] as const,
    },
    projects: {
      list: () => ['ws', wsId, 'projects', 'list'] as const,
      detail: (id: number) => ['ws', wsId, 'projects', 'detail', id] as const,
      rows: (id: number, filters?: Filters) =>
        ['ws', wsId, 'projects', 'rows', id, filters] as const,
      // rowsAll: prefix-only key for invalidation that should match every
      // filter combination. `rows(id)` without args produces an explicit
      // `undefined` 6th element, which react-query treats as a literal —
      // calls with a real filters object don't match.
      rowsAll: (id: number) => ['ws', wsId, 'projects', 'rows', id] as const,
    },
    notifications: {
      list: () => ['ws', wsId, 'notifications', 'list'] as const,
      unread: () => ['ws', wsId, 'notifications', 'unread'] as const,
    },
    data: {
      tasks: (filters?: Filters) => ['ws', wsId, 'data', 'tasks', filters] as const,
      // tasksAll: see rowsAll comment — prefix-only key so invalidation
      // matches every filtered TasksSection variant on a project page.
      // It also covers the stat-card counters below (same prefix), so a
      // tasksAll() invalidation refreshes the cards alongside the table.
      tasksAll: () => ['ws', wsId, 'data', 'tasks'] as const,
      statsCounts: (filters?: Filters) =>
        ['ws', wsId, 'data', 'tasks', 'stats', filters] as const,
      recentResults: (limit: number) =>
        ['ws', wsId, 'data', 'recent-results', limit] as const,
    },
    quick: {
      files: () => ['ws', wsId, 'quick', 'files'] as const,
      tasks: (filters?: Filters) => ['ws', wsId, 'quick', 'tasks', filters] as const,
    },
    overlay: {
      // tasksAll: prefix-only key — mutations must invalidate every page, not
      // just the one the user happens to be on (same trick as data.tasksAll).
      tasksAll: () => ['ws', wsId, 'overlay', 'tasks'] as const,
      tasks: (page: number, pageSize: number) =>
        ['ws', wsId, 'overlay', 'tasks', page, pageSize] as const,
      stats: () => ['ws', wsId, 'overlay', 'stats'] as const,
      detail: (id: number) => ['ws', wsId, 'overlay', 'detail', id] as const,
    },
    transcription: {
      list: () => ['ws', wsId, 'transcription', 'list'] as const,
    },
    translate: {
      keys: () => ['ws', wsId, 'translate', 'keys'] as const,
      usage: () => ['ws', wsId, 'translate', 'usage'] as const,
    },
    templates: {
      list: () => ['ws', wsId, 'templates', 'list'] as const,
    },
    settings: {
      apikeys: () => ['ws', wsId, 'settings', 'apikeys'] as const,
    },
    stats: {
      today: () => ['ws', wsId, 'stats', 'today'] as const,
      realtime: () => ['ws', wsId, 'stats', 'realtime'] as const,
      mini: () => ['ws', wsId, 'stats', 'mini'] as const,
      elCapabilities: () =>
        ['ws', wsId, 'stats', 'el-capabilities'] as const,
    },
    presets: {
      list: () => ['ws', wsId, 'presets', 'list'] as const,
    },
    labels: {
      list: () => ['ws', wsId, 'labels', 'list'] as const,
    },
    workspace: {
      members: () => ['ws', wsId, 'workspace', 'members'] as const,
    },
  }),
  global: {
    version: () => ['global', 'config', 'version'] as const,
    config: {
      engines: () => ['global', 'config', 'engines'] as const,
      limits: () => ['global', 'config', 'limits'] as const,
      all: () => ['global', 'config', 'all'] as const,
    },
    // Authenticated user's workspace list — ws-independent (it IS the list of
    // available workspaces), so no `'ws'` prefix.
    workspaces: () => ['global', 'workspaces'] as const,
  },
  admin: {
    users: () => ['admin', 'users'] as const,
    userDetail: (id: number) => ['admin', 'users', 'detail', id] as const,
    errors: () => ['admin', 'errors'] as const,
    stats: () => ['admin', 'stats'] as const,
    workers: () => ['admin', 'workers'] as const,
    audit: () => ['admin', 'audit'] as const,
  },
} as const
